import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { createContext, useState, useContext, useMemo, useId, } from 'preact/compat';
import { useFormContext } from 'react-hook-form';
import { useCallback, useRef } from 'react';
import Messages from '@genericBookings/components/Messages';
import StyledForm from '@bookingForm/styles/form';
import useGlobalEvent from 'beautiful-react-hooks/useGlobalEvent';
import { useStepCompletion } from '@widgets/DayTickets/context/Steps';
import { ThemeContext } from 'styled-components';
import Notice from '@widgets/shared/components/Notice';
import SuccessMessage from '@genericBookings/components/SuccessMessage/SuccessMessage';
import { useAppDispatch, useAppSelector } from '@genericBookings/store/hooks';
import { getActivities, getFetchingStatus, setActivities } from '@genericBookings/store/reducers/activities';
import basketResponseEvent from '@genericBookings/basketResponseEvent';
import LoadingSpinner from '@widgets/shared/components/LoadingSpinner';
import { DummyLink } from '@genericBookings/components/SelectedOption/styles';
import { useMessage } from '@genericBookings/context/Message';
import { defaultVisitors } from '@genericBookings/types/Visitors';
import useDidMount from 'beautiful-react-hooks/useDidMount';
import useQueryParams from '@widgets/DIYDayTickets/hooks/useQueryParams';
import { OptionsCTX } from '@bookingForm/context/Options';
import visitDateEvent, { visitDateSelectedEvent } from '@genericBookings/primaryActionEvents';
import useGetFormDataAsParams from '@genericBookings/hooks/useGetFormDataAsParams';
import clsx from 'clsx';
const MessageContext = createContext({
    isSubmitting: false,
    setIsSubmitting: (isSubmitting) => null,
    paymentComplete: false,
    setPaymentComplete: (hasPaymentSucceeded) => null,
    errorModalContent: '',
    setErrorModalContent: (errorModalContent) => null,
    errorModalOpen: false,
    setErrorModalOpen: (errorModalOpen) => null,
    calendarHighlighted: false,
    setCalendarHighlighted: (calendarHighlighted) => null,
    reservationFailed: false,
    setReservationFailed: (errorModalOpen) => null,
    handleSubmitEvent: () => null,
    datepickerOpen: false,
    setDatepickerOpen: (datePickerOpen) => null,
});
export const useFieldsWrapper = () => {
    const value = useContext(MessageContext);
    return value ?? {};
};
function FieldsWrapperProvider({ options: optionsContext, children, endpoint, productId, }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [errorModalContent, setErrorModalContent] = useState('');
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [reservationFailed, setReservationFailed] = useState(false);
    const [showValues, setShowValues] = useState(false);
    const [showActivities, setShowActivities] = useState(false);
    const [showLoginURL, setShowLoginURL] = useState(false);
    const [showSteps, setShowSteps] = useState(false);
    const [datepickerOpen, setDatepickerOpen] = useState(false);
    // only used on the booking form on SWW.
    const [calendarHighlighted, setCalendarHighlighted] = useState(false);
    const ref = useRef(null);
    const activities = useAppSelector(getActivities);
    const dispatch = useAppDispatch();
    const onReservationFailed = useGlobalEvent('reservation.failed');
    const getLoginParams = useGetFormDataAsParams();
    const loginURL = useMemo(() => {
        const params = getLoginParams();
        const currentPage = new URL(window.location.href);
        currentPage.search = params.toString();
        return currentPage.toString();
    }, [getLoginParams]);
    const { modifyStep, steps, stepName, sessionStep, } = useStepCompletion();
    onReservationFailed((ev) => {
        const updateActivities = activities.map((curr) => ({
            ...curr,
            activeSession: undefined,
        }));
        dispatch(setActivities(updateActivities));
        modifyStep(sessionStep, false);
        setReservationFailed(true);
        setErrorModalOpen(true);
        setErrorModalContent(ev.detail);
    });
    const options = useContext(optionsContext);
    const { bookUrl, showLabels, basketUrl, onComplete, debug, isMulti, isBuilder, loadingSessionsMessage, loadingActivitiesMessage, readingProductMessage, redirect, } = options;
    const formKey = useId();
    const { handleSubmit, getValues, } = useFormContext();
    const { isLastStep, step, } = useStepCompletion();
    const handleErrorOpen = useCallback(() => setErrorModalOpen(true), [setErrorModalOpen]);
    const handleErrorClose = useCallback(() => {
        setErrorModalOpen(false);
        setErrorModalContent('');
    }, [setErrorModalOpen]);
    const onPaymentSuccess = useGlobalEvent('payment.success');
    const onPaymentError = useGlobalEvent('payment.error');
    onPaymentSuccess(() => setPaymentComplete(true));
    onPaymentError((event) => {
        setErrorModalContent(event.detail.errorMessage);
        setErrorModalOpen(true);
    });
    const handleSubmitEvent = useCallback(() => {
        setIsSubmitting(true);
        endpoint()
            .then((res) => {
            basketResponseEvent(res, basketUrl, redirect);
            if (res.success) {
                onComplete();
            }
        })
            .finally(() => {
            setIsSubmitting(false);
        });
    }, [basketUrl, endpoint, onComplete, redirect]);
    const { requestingData, requestingSessions, requestingPriceBands, requestingProductBands, requestingActivities, } = useAppSelector(getFetchingStatus);
    const { layout: maybeLayout, } = useContext(ThemeContext);
    const { accent, } = useContext(ThemeContext);
    const { addMessage } = useMessage();
    const layout = maybeLayout ? maybeLayout : 'rework';
    const onDayticketOverlaps = useGlobalEvent('dayticket.overlaps');
    const { visitDate: paramVisitDate } = useQueryParams(OptionsCTX);
    const onHandleBundleSelected = useGlobalEvent('bundle.select');
    onHandleBundleSelected((ev) => { handleComponentScrolledTo(); });
    const handleComponentScrolledTo = useCallback(() => {
        setCalendarHighlighted(layout === 'winter-wonderland');
    }, [layout, setCalendarHighlighted]);
    const handleDidMount = useCallback(() => {
        if (!isMulti && !isBuilder) {
            const currActivity = {
                activity: {
                    id: productId,
                    label: '',
                    sublabel: '',
                    durationInMinutes: 0,
                    priceBands: [],
                },
                sessions: [],
                activeSession: undefined,
                visitors: defaultVisitors,
                selected: false,
            };
            dispatch(setActivities([currActivity]));
        }
        if (!paramVisitDate) {
            visitDateEvent(isMulti, handleComponentScrolledTo);
        }
        else {
            visitDateSelectedEvent();
        }
        if (isMulti && !isBuilder) {
            const { activities: initialActivities } = options;
            dispatch(setActivities(initialActivities.map((curr) => ({
                activity: curr,
                activeSession: undefined,
                sessions: [],
            }))));
        }
    }, [isMulti, isBuilder, paramVisitDate, productId, dispatch, handleComponentScrolledTo, options]);
    useDidMount(handleDidMount);
    const loadingMessage = useMemo(() => {
        if (requestingSessions) {
            return loadingSessionsMessage ?? 'Loading Sessions...';
        }
        if (requestingPriceBands || requestingProductBands) {
            return readingProductMessage ?? 'Loading...';
        }
        if (requestingActivities) {
            return loadingActivitiesMessage ?? 'Loading Activities...';
        }
        return 'Loading...';
    }, [loadingActivitiesMessage, loadingSessionsMessage, readingProductMessage, requestingActivities, requestingPriceBands, requestingProductBands, requestingSessions]);
    if (isMulti) {
        // show toast when use clicks an overlapping time
        onDayticketOverlaps(() => {
            addMessage({
                type: 'warning',
                closable: true,
                duration: 3000,
                text: 'The time you have selected conflicts with another time you have chosen',
            });
        });
    }
    const formClasses = clsx(`step--${stepName}`, requestingData ? 'form--loading' : '', `form--${layout}`, !isMulti ? 'form--single' : 'form--multi', isBuilder ? 'form--builder' : '');
    const value = useMemo(() => ({
        isSubmitting,
        setIsSubmitting,
        paymentComplete,
        setPaymentComplete,
        errorModalContent,
        setErrorModalContent,
        errorModalOpen,
        setErrorModalOpen,
        handleSubmitEvent,
        reservationFailed,
        setReservationFailed,
        datepickerOpen,
        setDatepickerOpen,
        calendarHighlighted,
        setCalendarHighlighted,
    }), [
        isSubmitting,
        paymentComplete,
        errorModalContent,
        errorModalOpen,
        handleSubmitEvent,
        reservationFailed,
        datepickerOpen,
        calendarHighlighted,
    ]);
    return (_jsxs(MessageContext.Provider, { value: value, children: [_jsx(SuccessMessage, { options: optionsContext, visible: paymentComplete }), _jsx(StyledForm.Wrapper, { action: bookUrl, method: "POST", onSubmit: handleSubmit(handleSubmitEvent), ref: ref, noValidate: true, style: { display: paymentComplete ? 'none' : 'block' }, isLastStep: isLastStep, layout: layout, labelsShown: showLabels, className: formClasses, children: _jsxs(StyledForm.Fields, { children: [requestingData && (_jsxs(StyledForm.LoadingWrapper, { children: [_jsx(LoadingSpinner, { colourOverride: layout === 'winter-wonderland' ? '#000' : accent }), _jsx("span", { className: "h6", children: loadingMessage })] })), _jsx(Messages, {}), children] }) }, `form-${formKey}`), (errorModalOpen && errorModalContent) && (_jsx(Notice, { onClose: handleErrorClose, content: errorModalContent })), debug && (_jsx("div", { style: { marginTop: 20 }, className: "container container--small", children: _jsxs("div", { className: "rfx-grid grid", children: [_jsxs("div", { className: "rfx-col-md-4 rfx-col-6", children: [_jsx("div", { children: _jsxs(DummyLink, { onClick: () => setShowValues(!showValues), children: [showValues ? 'Hide' : 'Show', ' ', "Values"] }) }), showValues && (_jsx("pre", { children: JSON.stringify(getValues(), null, 2) }))] }), _jsxs("div", { className: "rfx-col-md-4 rfx-col-6", children: [_jsx("div", { children: _jsxs(DummyLink, { onClick: () => setShowActivities(!showActivities), children: [showActivities ? 'Hide' : 'Show', ' ', "Activities"] }) }), showActivities && (_jsx("pre", { children: JSON.stringify(activities, null, 2) }))] }), _jsxs("div", { className: "rfx-col-md-4 rfx-col-6", children: [_jsx("div", { children: _jsxs(DummyLink, { onClick: () => setShowSteps(!showSteps), children: [showSteps ? 'Hide' : 'Show', ' ', "Steps"] }) }), showSteps && (_jsx("pre", { children: JSON.stringify({
                                        steps,
                                        isLastStep,
                                    }, null, 2) }))] }), _jsxs("div", { className: "rfx-col-md-4 rfx-col-6", children: [_jsx("div", { children: _jsxs(DummyLink, { onClick: () => setShowLoginURL(!showLoginURL), children: [showLoginURL ? 'Hide' : 'Show', ' ', "Success URL"] }) }), showLoginURL && (_jsx("pre", { children: loginURL }))] }), paymentComplete && (_jsx("div", { className: "rfx-col-md-4 rfx-col-6", children: _jsx(DummyLink, { onClick: () => setPaymentComplete(false), children: "Payment Incomplete" }) }))] }) }))] }));
}
export default FieldsWrapperProvider;
